import { Photo } from '@capacitor/camera';
import { ImageContent } from '../providers/llm.service';

/**
 * Convert an image file to base64 string for use with LLM autosuggest API
 * @param file File or Blob object (e.g., from file input or camera)
 * @returns Promise with base64 string (without data URL prefix)
 */
export function imageToBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data:image/...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Get media type from file
 * @param file File or Blob object
 * @returns Media type string for autosuggest API
 */
export function getImageMediaType(file: File | Blob): 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp' {
  const type = file.type as string;
  if (type === 'image/jpeg' || type === 'image/jpg') {
    return 'image/jpeg';
  }
  if (type === 'image/png') {
    return 'image/png';
  }
  if (type === 'image/gif') {
    return 'image/gif';
  }
  if (type === 'image/webp') {
    return 'image/webp';
  }
  // Default to jpeg if unknown
  return 'image/jpeg';
}

/**
 * Get media type from Capacitor Photo format
 * @param photo Photo object from Capacitor Camera
 * @returns Media type string for autosuggest API
 */
export function getPhotoMediaType(photo: Photo): 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp' {
  const format = photo.format?.toLowerCase() || 'jpeg';

  if (format === 'jpeg' || format === 'jpg') {
    return 'image/jpeg';
  }
  if (format === 'png') {
    return 'image/png';
  }
  if (format === 'gif') {
    return 'image/gif';
  }
  if (format === 'webp') {
    return 'image/webp';
  }

  // Default to jpeg if unknown
  return 'image/jpeg';
}

/**
 * Convert a Capacitor Photo to ImageContent format for LLM requests
 * @param photo Photo object from Capacitor Camera
 * @returns ImageContent object ready for LLM autosuggest API
 */
export function photoToImageContent(photo: Photo): ImageContent {
  return {
    type: 'image',
    source: {
      type: 'base64',
      media_type: getPhotoMediaType(photo),
      data: photo.base64String // Capacitor Photo already provides base64 without prefix
    }
  };
}

/**
 * Create a text content block
 * @param text The text content
 * @returns TextContent object
 */
export function createTextContent(text: string) {
  return {
    type: 'text' as const,
    text
  };
}
